import React, { useEffect, useState } from "react";
import CustomInput from "../../Components/CommonComponents/CustomInput";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getJwtToken, END_POINT, setClinicProfile } from "../../Redux/AdminReducer/action";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import RightWrapper from "../../Components/CommonComponents/ComponentWrapper";
import CameraCapture from "../../Components/CommonComponents/CameraCapture";
import ResetPasswordPopup from "./ResetPassword";
import ContactUsPopup from "./ContactUsEmail";
import { useDispatch, useSelector } from "react-redux";
import { BsBuilding, BsPerson, BsShieldLock, BsArrowLeft, BsHeadset } from "react-icons/bs";
import { GrPowerReset, GrContactInfo } from "react-icons/gr";
import { FaCrown } from "react-icons/fa";
import { SubscriptionPlanView } from "./SubscriptionPlan";
import WebsiteSpecialityCard from "./WebsiteSpecialityCard";

const fetchSingleItem = async () => {
  try {
    const result = await axios.get(`${END_POINT}/clinic-profile/`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    console.error(error);
    toast.error(error?.response?.data || "Something went wrong while fetching clinic profile");
  }
};

const createItem = async (data, setIsProcessing) => {
  try {
    await axios.post(`${END_POINT}/clinic-profile`, data, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    setIsProcessing(false);
    toast.success("Clinic Profile saved successfully!");
    return true;
  } catch (error) {
    setIsProcessing(false);
    toast.error(error?.response?.data || "Something went wrong while saving clinic profile");
    return false;
  }
};

const initialFormData = {
  ClinicName: "",
  DoctorName: "",
  ClinicContact: "",
  ClinicEmail: "",
  Logo: "",
};

const ClinicProfile = ({ defaultTab }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialTab =
    defaultTab || (location.pathname.includes("login-profile") ? "user" : "clinic");
  const [activeTab, setActiveTab] = useState(initialTab);
  const [formData, setFormData] = useState();
  const [formError, setFormError] = useState(initialFormData);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isResetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [isContactUsOpen, setContactUsOpen] = useState(false);

  const loggedInUser = useSelector((state) => state?.AuthReducer?.userLogindata?.data);
  const dispatch = useDispatch();
  const logoInputRef = React.useRef(null);
  const [capturedLogo, setCapturedLogo] = useState(null);

  useEffect(() => {
    fetchSingleItem().then((e) => {
      setFormData(e?.data);
    });
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, data: { ...prev?.data, [name]: value } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    const form = e.target;
    let formdi = new FormData(form);
    if (capturedLogo) formdi.set("Logo", capturedLogo);
    const saved = await createItem(formdi, setIsProcessing);

    if (saved) {
      const fresh = await fetchSingleItem();
      setFormData(fresh?.data);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      setCapturedLogo(null);
      dispatch(setClinicProfile());
    }
  };

  return (
    <RightWrapper>
      <div className="py-2 px-2 md:px-4 w-full">
        {/* Top Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs md:text-sm font-medium py-1.5 px-3 rounded-lg shadow-sm transition"
          >
            <BsArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">Profile Settings</h2>
            <p className="text-xs text-slate-500">
              Manage your clinic details and user account settings
            </p>
          </div>
        </div>

        {/* Tab Buttons (Pill Segmented Control) */}
        <div className="inline-flex p-1 bg-slate-200/80 rounded-xl mb-4 gap-1">
          <button
            type="button"
            onClick={() => setActiveTab("clinic")}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-medium text-xs md:text-sm transition-all ${
              activeTab === "clinic"
                ? "bg-white text-slate-900 shadow-sm font-semibold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <BsBuilding className="w-4 h-4 text-sky-600" />
            <span>Clinic Profile</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("user")}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-medium text-xs md:text-sm transition-all ${
              activeTab === "user"
                ? "bg-white text-slate-900 shadow-sm font-semibold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <BsPerson className="w-4 h-4 text-sky-600" />
            <span>User Profile</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("subscription")}
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-lg font-medium text-xs md:text-sm transition-all ${
              activeTab === "subscription"
                ? "bg-white text-slate-900 shadow-sm font-semibold"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <FaCrown className="w-4 h-4 text-amber-500" />
            <span>Subscription Plan</span>
          </button>
        </div>

        {/* Tab Content: Clinic Profile */}
        {activeTab === "clinic" && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-6">
            <div className="border-b border-slate-100 pb-3 mb-4">
              <h3 className="text-base md:text-lg font-semibold text-slate-800">Clinic Information</h3>
              <p className="text-xs text-slate-500">Update clinic branding, contact, and doctor details</p>
            </div>

            <form onSubmit={handleSubmit} method="post" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
                <CustomInput
                  label="Clinic Name"
                  name="ClinicName"
                  type="text"
                  value={formData?.data?.ClinicName || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Clinic Name"
                  error={formError.ClinicName}
                />
                <CustomInput
                  label="Doctor Name"
                  name="DoctorName"
                  type="text"
                  value={formData?.data?.DoctorName || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Doctor Name"
                  error={formError.DoctorName}
                />
                <CustomInput
                  label="Clinic Contact"
                  name="ClinicContact"
                  type="tel"
                  value={formData?.data?.ClinicContact || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Clinic Contact"
                  error={formError.ClinicContact}
                />
                <CustomInput
                  label="Clinic Email"
                  name="ClinicEmail"
                  type="email"
                  value={formData?.data?.ClinicEmail || ""}
                  onChange={handleInputChange}
                  placeholder="Enter Clinic Email"
                  error={formError.ClinicEmail}
                />
              </div>

              {/* Logo Section */}
              <div className="pt-2">
                <label className="block mb-1 text-xs md:text-sm font-medium text-gray-700">
                  Clinic Logo
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-4 p-3.5 rounded-xl border border-slate-200 bg-slate-50/70">
                  {formData?.imageData ? (
                    <div className="w-20 h-20 p-1.5 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xs">
                      <img
                        src={`data:image/jpeg;base64,${formData.imageData}`}
                        alt="Clinic Logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 bg-slate-100 border border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 flex-shrink-0">
                      <BsBuilding className="w-6 h-6 mb-1 text-slate-400" />
                      <span className="text-[10px]">No Logo</span>
                    </div>
                  )}
                  <div className="flex-1 w-full min-w-0">
                    <input
                      ref={logoInputRef}
                      name="Logo"
                      type="file"
                      accept="image/*"
                      className="w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100 border border-slate-200 rounded-lg p-1 bg-white cursor-pointer"
                      onChange={() => setCapturedLogo(null)}
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <CameraCapture
                        onCapture={(file) => {
                          if (logoInputRef.current) logoInputRef.current.value = "";
                          setCapturedLogo(file);
                        }}
                      />
                      {capturedLogo && <span className="text-[11px] text-slate-600 truncate">Captured: {capturedLogo.name}</span>}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Upload JPEG, PNG or JPG image for prescription and report headers</p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-3">
                <button
                  type="submit"
                  disabled={isProcessing}
                  className="inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm px-6 py-2.5 rounded-lg shadow-sm transition disabled:opacity-50 cursor-pointer"
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Clinic Profile</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Website: Speciality section image (public site) */}
        {activeTab === "clinic" && <WebsiteSpecialityCard />}

        {/* Tab Content: User Profile */}
        {activeTab === "user" && (
          <div className="space-y-4">
            {/* User Overview Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-bold text-xl border border-sky-200 flex-shrink-0">
                  {loggedInUser?.name ? loggedInUser.name.charAt(0).toUpperCase() : <BsPerson className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-lg font-bold text-slate-900 truncate">
                      {loggedInUser?.name || "User"}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-800 capitalize">
                      {loggedInUser?.role || "Staff"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{loggedInUser?.email || "No email on record"}</p>
                </div>
              </div>
            </div>

            {/* Account Information Details */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <div className="border-b border-slate-100 pb-3 mb-4">
                <h4 className="text-base font-semibold text-slate-800">Account Details</h4>
                <p className="text-xs text-slate-500">Your profile credentials in the clinic system</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
                <CustomInput
                  label="Full Name"
                  name="userName"
                  type="text"
                  value={loggedInUser?.name || ""}
                  disabled={true}
                  placeholder="Full Name"
                />
                <CustomInput
                  label="Email Address"
                  name="userEmail"
                  type="email"
                  value={loggedInUser?.email || ""}
                  disabled={true}
                  placeholder="Email Address"
                />
                <CustomInput
                  label="Assigned Role"
                  name="userRole"
                  type="text"
                  value={loggedInUser?.role || ""}
                  disabled={true}
                  placeholder="Role"
                />
              </div>
            </div>

            {/* Security & Password Reset */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <BsShieldLock className="w-4 h-4 text-sky-600" />
                    <h4 className="text-base font-semibold text-slate-800">Account Security</h4>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Manage your password and security credentials
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setResetPasswordOpen(true)}
                  className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-medium text-xs md:text-sm py-2 px-4 rounded-lg shadow-sm transition cursor-pointer"
                >
                  <GrPowerReset className="w-3.5 h-3.5" />
                  <span>Reset Password</span>
                </button>
              </div>
            </div>

            {/* Support & Contact Us */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <BsHeadset className="w-4 h-4 text-sky-600" />
                    <h4 className="text-base font-semibold text-slate-800">Support</h4>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Reach out to us for help with your account or the clinic system
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setContactUsOpen(true)}
                  className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white font-medium text-xs md:text-sm py-2 px-4 rounded-lg shadow-sm transition cursor-pointer"
                >
                  <GrContactInfo className="w-3.5 h-3.5" />
                  <span>Contact Us</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: Subscription Plan */}
        {activeTab === "subscription" && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-6">
            <SubscriptionPlanView embedded={true} />
          </div>
        )}

        {/* Password Reset Popup Modal */}
        <ResetPasswordPopup isOpen={isResetPasswordOpen} onClose={() => setResetPasswordOpen(false)} />
        <ContactUsPopup isOpen={isContactUsOpen} onClose={() => setContactUsOpen(false)} />
        <ToastContainer />
      </div>
    </RightWrapper>
  );
};

export default ClinicProfile;
